import { ApiError, SessionExpiredError, translationApi } from '@/api/client';
import type { NotifLang } from '@/types/admin.types';
import { LANG_OPTIONS } from '../NotificationsTab/LangBar';

/**
 * Lógica pura del botón "Traducir a los demás idiomas" de los modales del Catálogo de
 * Fundamentales (Fundamental, Actitud y Comportamiento, Daily Habit). Reusa `translationApi`
 * (Azure Translator vía SurveyService, remapea pt → pt-pt) — nunca llama al traductor por su cuenta.
 *
 * Regla de negocio: SOLO se llenan los idiomas vacíos; lo ya capturado jamás se sobrescribe.
 */

export const CATALOG_LANGS = LANG_OPTIONS.map(l => l.id) as NotifLang[];

/** Texto de un campo en cada uno de los 7 idiomas. */
export type LangTexts = Record<NotifLang, string>;
/** Campos de un modal por nombre (p. ej. `names`, `shortNames`, `descriptions`). */
export type FieldMap = Record<string, LangTexts>;
/** Traducciones obtenidas, por campo → idioma. Solo trae idiomas que estaban vacíos. */
export type FilledMap = Record<string, Partial<Record<NotifLang, string>>>;

export type TranslateErrorKind = 'session' | 'wait' | 'retry';

interface PlanItem { field: string; text: string; targets: NotifLang[]; }
export interface TranslationBatch { sourceLang: NotifLang; items: PlanItem[]; }

/**
 * Decide qué traducir. Idioma origen de cada campo: el del chip activo si ya tiene texto; si no, el
 * primero con texto (el usuario puede haber escrito en cualquier idioma). Un campo sin texto en
 * ningún idioma, o sin idiomas vacíos por llenar, no entra. Los campos con el mismo origen viajan
 * juntos en una sola llamada.
 */
export function planTranslation(fields: FieldMap, activeLang: NotifLang): TranslationBatch[] {
  const bySource = new Map<NotifLang, PlanItem[]>();
  for (const [field, texts] of Object.entries(fields)) {
    const hasText = (lang: NotifLang) => (texts[lang] ?? '').trim() !== '';
    const sourceLang = hasText(activeLang) ? activeLang : CATALOG_LANGS.find(hasText);
    if (!sourceLang) continue;
    const targets = CATALOG_LANGS.filter(lang => lang !== sourceLang && !hasText(lang));
    if (targets.length === 0) continue;
    const bucket = bySource.get(sourceLang) ?? [];
    bucket.push({ field, text: texts[sourceLang].trim(), targets });
    bySource.set(sourceLang, bucket);
  }
  return [...bySource].map(([sourceLang, items]) => ({ sourceLang, items }));
}

/** Copia de `current` con `filled` aplicado únicamente a los idiomas que sigan vacíos. */
export function mergeMissing(current: LangTexts, filled?: Partial<Record<NotifLang, string>>): LangTexts {
  if (!filled) return current;
  const next = { ...current };
  for (const lang of CATALOG_LANGS) {
    if (next[lang].trim() === '' && filled[lang]) next[lang] = filled[lang] as string;
  }
  return next;
}

/** Azure respondió sin ninguna traducción (el backend devuelve 200 vacío cuando Azure falla). */
export class EmptyTranslationError extends Error {
  constructor() {
    super('El traductor no devolvió ninguna traducción');
  }
}

export interface TranslationResult {
  filled: FilledMap;
  /** Cuántos idiomas pedidos volvieron vacíos. */
  missing: number;
}

export async function runTranslation(batches: TranslationBatch[]): Promise<TranslationResult> {
  const filled: FilledMap = {};
  let requested = 0;
  let received = 0;
  for (const { sourceLang, items } of batches) {
    const targetLangs = CATALOG_LANGS.filter(lang => items.some(item => item.targets.includes(lang)));
    const response = await translationApi.translateTexts({
      sourceLang, targetLangs, texts: items.map(item => item.text),
    });
    items.forEach((item, index) => {
      for (const target of item.targets) {
        requested += 1;
        const value = response.translations[target]?.[index]?.trim();
        if (!value) continue;
        received += 1;
        filled[item.field] = { ...filled[item.field], [target]: value };
      }
    });
  }
  if (requested > 0 && received === 0) throw new EmptyTranslationError();
  return { filled, missing: requested - received };
}

/**
 * Qué le pedimos al usuario según el fallo:
 * - `session`: sesión vencida → recargar.
 * - `wait`: el servicio está caído/saturado o sin respuesta (red, 5xx, 429, 408, traductor vacío) → esperar un momento.
 * - `retry`: cualquier otro rechazo (4xx) → intentar de nuevo.
 */
export function classifyTranslateError(error: unknown): TranslateErrorKind {
  if (error instanceof SessionExpiredError) return 'session';
  if (error instanceof ApiError) {
    return error.status >= 500 || error.status === 429 || error.status === 408 ? 'wait' : 'retry';
  }
  return 'wait';
}
